// /people/:id — bir kişinin kaydı, KENDİ SAYFASI (SEM-6354).
//
// Bu ekran daha önce listenin üstünde bir çekmeceydi. Çekmece bir yer değil,
// bir kesintidir: paylaşılacak bir adresi yoktur, yeni sekmede açılmaz, geri
// düğmesi onu kapatmaz ve içine ikinci bir bağlantı konulamaz — çapraz-bağlantı
// hattının (SEM-6329) takıldığı yer tam olarak buydu. Sayfa olduğu an yönetici
// ve departman bağlantıları sıradan bağlantı hâline gelir.
//
// Çizim kaynağı: NovemHR-ekranlar screens/emp-directory-detail.jsx (hero +
// iletişim/organizasyon kartları) ve personnel.jsx 7b–7g (kayıt sekmeleri,
// employee-detail-tabs.tsx). Sekmeler `?tab=` ile bu sayfanın İÇİNDEDİR: hedefleri
// menü girdisi değil, aynı kişinin kaydının bölümleri. Çizimde olup ucu olmayan
// her şey ATLANDI, uydurulmadı (port kuralı 8): dahili/mobil telefon,
// Teams/Slack, ofis-kat-oda, doğum günü/yıldönümü sayaçları, etkileşim
// istatistikleri, özlük PDF'i.
//
// İKİ YÜZ, TEK ADRES (E41). `/people/:id` iki şeyi gösterir: personel kaydı ve
// rehber kartı. Personel kaydını sunucu kapsamlar — düz bir çalışan başkasının
// kaydını okuyamaz (403) — ama bir kişinin adı her yerde bu adrese bağlanır
// (yönetici adı, departman listesi, görev ataması). O yüzden sayfa, kaydı
// okuyamayan herkese rehber kartını (ad, ünvan, departman, iş e-postası) gösterir:
// önce kartı isteyen bir çalışan "Buna erişiminiz yok" hata sayfası görüyordu.
// Çalışan kapsamındaki çağıran için personel isteği hiç atılmaz (cevabı bellidir);
// diğerleri önce kaydı dener ve yalnız 403'te karta düşer.

import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink, useLocation, useParams } from "react-router";
import { ArrowLeft, Mail, Pencil } from "lucide-react";

import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  ErrorState,
  FieldRow,
  Loading,
  PageHeader,
  SectionTitle,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from "@novemai/components";

import { EnvelopeError, errorMessage } from "@/api/errors";
import { useScopeTierState } from "@/api/use-scope-tier";
import { DepartmentLink } from "@/components/department-link";
import { useTabTitle } from "@/components/TabStrip";
import { fetchEmployee } from "@/features/employees/api";
import {
  AssetsTab,
  AttendanceTab,
  DocumentsTab,
  EMPLOYEE_DETAIL_TABS,
  FamilyTab,
  LeaveTab,
  PayrollTab,
  PersonActions,
  PersonnelFileTab,
} from "@/features/employees/employee-detail-tabs";
import {
  DIRECTORY_KEY,
  fetchDirectoryEntry,
  type DirectoryEntry,
} from "@/features/employees/directory-api";
import {
  fullName,
  initialsOf,
  LIFECYCLE_KEY,
  LIFECYCLE_TONE,
} from "@/features/employees/employee-display";
import { formatDate, formatTenureDuration } from "@/lib/date-range";
import { useTabParam } from "@/lib/use-tab-param";

function isForbidden(error: unknown): boolean {
  return error instanceof EnvelopeError && error.httpStatus === 403;
}

export function EmployeeDetailContent(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const { id = "" } = useParams();
  // The list handed its filter over in the query string; the return link hands
  // it back, so a filtered list is still filtered on the way home (SEM-6350).
  const { search } = useLocation();

  // An employee_self caller can never read another person's personnel record
  // (the server answers 403), so that request is not sent for them. An unresolved
  // or failed tier lookup is not "employee_self": those callers try the record
  // and fall back to the card only on a 403.
  const scope = useScopeTierState();
  const cardOnly = scope.tier === "employee_self";
  const [tab, setTab] = useTabParam("tab", EMPLOYEE_DETAIL_TABS, "ozluk");

  const detail = useQuery({
    queryKey: ["employees", "detail", id],
    queryFn: () => fetchEmployee(id),
    enabled: id !== "" && !scope.isLoading && !cardOnly,
  });
  const recordDenied = isForbidden(detail.error);

  const card = useQuery({
    queryKey: [...DIRECTORY_KEY, "card", id],
    queryFn: () => fetchDirectoryEntry(id),
    enabled: id !== "" && (cardOnly || recordDenied),
  });

  const row = detail.data;
  const entry = card.data;
  const showsCard = cardOnly || recordDenied;

  // The frame names the page the reader is on. Before this the error frame kept
  // the personnel-list title, so a refused card read "Çalışanlar yüklenemedi"
  // under a "Çalışanlar" tab, as if the list had failed.
  const frameTitle = showsCard ? t("people.detail.card_title") : t("people.detail.title");
  const name = row !== undefined ? fullName(row) : entry !== undefined ? fullName(entry) : null;
  useTabTitle(name ?? frameTitle);

  const none = t("people.detail.none");

  // The card belongs to the directory and the record to the personnel list: each
  // return link goes back to the surface the reader came from.
  const backAction = (
    <Button variant="ghost" asChild>
      <RouterLink to={cardOnly ? "/me/directory" : `/people${search}`}>
        <ArrowLeft size={14} aria-hidden="true" />
        {cardOnly ? t("people.detail.back_directory") : t("people.detail.back")}
      </RouterLink>
    </Button>
  );

  // Until a person arrives the page has no name to be titled with, so every
  // pre-person state renders the same generic frame.
  const framed = (body: React.ReactNode): React.ReactElement => (
    <div className="mx-auto max-w-[1100px] space-y-6">
      <PageHeader title={frameTitle} actions={backAction} />
      {body}
    </div>
  );

  if (row === undefined && entry !== undefined) {
    return <DirectoryCard entry={entry} backAction={backAction} />;
  }

  // The card's own failure wins; otherwise the record's, unless that was the
  // 403 the card fallback exists to absorb.
  const failure = card.isError ? card.error : recordDenied ? null : detail.error;
  if (row === undefined && failure !== null) {
    // A refused record whose card cannot be shown either is a real denial. A
    // bare 404 from the card route alone means "not in the directory", the same
    // answer as for a person who does not exist, so it keeps the generic
    // not-found copy and the page is not an existence oracle.
    const denied = isForbidden(failure) || (recordDenied && card.isError);
    return framed(
      <ErrorState
        title={t("people.detail.error_title")}
        description={denied ? t("people.detail.denied") : errorMessage(failure, t)}
      />,
    );
  }
  // A missing person is an ERROR here, not an empty page: the route 404s and
  // unwrapEnvelope throws, so the branch above owns it. An absent row here
  // therefore only means "still resolving".
  if (row === undefined) {
    return framed(<Loading label={t("people.loading")} rows={5} />);
  }
  return (
    <div className="mx-auto max-w-[1100px] space-y-6">
      <PageHeader
        title={fullName(row)}
        description={row.job_title ?? t("people.cell.no_title")}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {backAction}
            <Button variant="secondary" asChild>
              <RouterLink to={`/people/${row.employee_id}/edit`}>
                <Pencil size={14} aria-hidden="true" />
                {t("people.detail.edit")}
              </RouterLink>
            </Button>
          </div>
        }
      />

      {/* Hero — çizimdeki künye şeridi (avatar · durum · sicil · kıdem) */}
      <Card>
        <CardBody>
          <div className="flex flex-wrap items-start gap-5">
            <Avatar size={64} alt={fullName(row)} initials={initialsOf(row)} />
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Badge tone={LIFECYCLE_TONE[row.lifecycle_state]}>
                  {t(LIFECYCLE_KEY[row.lifecycle_state])}
                </Badge>
                <span className="font-mono text-[12px] text-[var(--fg-muted)]">
                  {row.employee_number ?? none}
                </span>
              </div>
              <div className="font-serif text-[24px] tracking-[-0.01em] text-[var(--fg-primary)]">
                {fullName(row)}
              </div>
              <div className="mt-0.5 font-serif text-[14.5px] text-[var(--fg-secondary)]">
                {row.job_title ?? t("people.cell.no_title")}
              </div>
              {row.start_date !== null && (
                <div className="mt-3 text-[12.5px] text-[var(--fg-secondary)]">
                  {t("people.detail.tenure_line", {
                    tenure: formatTenureDuration(i18n.language, t, row.start_date),
                    start: formatDate(i18n.language, row.start_date),
                  })}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <PersonActions employeeId={row.employee_id} />
              <Button variant="secondary" asChild>
                <a href={`mailto:${row.primary_work_email}`}>
                  <Mail size={14} aria-hidden="true" />
                  {t("people.detail.send_email")}
                </a>
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      <Tabs value={tab} onValueChange={setTab} activation="manual">
        <TabList aria-label={t("people.detail.tabs.label")}>
          {EMPLOYEE_DETAIL_TABS.map((key) => (
            <Tab key={key} value={key}>
              {t(`people.detail.tabs.${key}`)}
            </Tab>
          ))}
        </TabList>
        <TabPanel value="ozluk">
          <PersonnelFileTab row={row} />
        </TabPanel>
        <TabPanel value="aile">
          <FamilyTab employeeId={row.employee_id} tier={scope.tier} />
        </TabPanel>
        <TabPanel value="belgeler">
          <DocumentsTab employeeId={row.employee_id} />
        </TabPanel>
        <TabPanel value="zimmet">
          <AssetsTab employeeId={row.employee_id} />
        </TabPanel>
        <TabPanel value="izin">
          <LeaveTab employeeId={row.employee_id} />
        </TabPanel>
        <TabPanel value="devam">
          <AttendanceTab employeeId={row.employee_id} />
        </TabPanel>
        <TabPanel value="bordro">
          <PayrollTab employeeId={row.employee_id} />
        </TabPanel>
      </Tabs>
    </div>
  );
}

// The directory card: what any colleague may see of a person. It carries only
// the fields the directory endpoint returns, so nothing here is a hidden
// personnel field. No sicil, status, tenure, manager or edit action, because the
// card is not the record. The department links to the directory roster, since the
// personnel list a plain employee is scoped to would answer with only themselves.
function DirectoryCard({
  entry,
  backAction,
}: {
  readonly entry: DirectoryEntry;
  readonly backAction: React.ReactNode;
}): React.ReactElement {
  const { t } = useTranslation();
  const none = t("people.detail.none");
  return (
    <div className="mx-auto max-w-[1100px] space-y-6">
      <PageHeader
        title={fullName(entry)}
        description={entry.job_title ?? t("people.cell.no_title")}
        actions={backAction}
      />

      <Card>
        <CardBody>
          <div className="flex flex-wrap items-start gap-5">
            <Avatar size={64} alt={fullName(entry)} initials={initialsOf(entry)} />
            <div className="min-w-0 flex-1">
              <div className="font-serif text-[24px] tracking-[-0.01em] text-[var(--fg-primary)]">
                {fullName(entry)}
              </div>
              <div className="mt-0.5 font-serif text-[14.5px] text-[var(--fg-secondary)]">
                {entry.job_title ?? t("people.cell.no_title")}
              </div>
            </div>
            <Button variant="secondary" asChild>
              <a href={`mailto:${entry.primary_work_email}`}>
                <Mail size={14} aria-hidden="true" />
                {t("people.detail.send_email")}
              </a>
            </Button>
          </div>
        </CardBody>
      </Card>

      <div className="grid items-start gap-6 lg:grid-cols-2">
        <Card>
          <CardBody>
            <SectionTitle>{t("people.detail.card_section_job")}</SectionTitle>
            <FieldRow label={t("people.detail.field_position")} value={entry.job_title ?? none} />
            <FieldRow
              label={t("people.detail.field_department")}
              value={
                entry.department_name === null ? (
                  none
                ) : (
                  <DepartmentLink departmentId={entry.department_id} surface="directory">
                    {entry.department_name}
                  </DepartmentLink>
                )
              }
            />
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <SectionTitle>{t("people.detail.section_contact")}</SectionTitle>
            <FieldRow label={t("people.detail.field_email")} value={entry.primary_work_email} />
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
